import { Eye } from "@ignite-analytics/icons";
import { Alert, Box, Chip, Container, Skeleton, Snackbar, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import MessagePage from "../../components/MessagePage";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { QuestionnaireTopBar } from "../../components/QuestionnaireTopBar";
import { useCodeOfConduct } from "../../hooks/useCodeOfConduct";
import { usePreviewFileActions } from "../../hooks/usePreviewFileActions";
import { usePreviewPrefill } from "../../hooks/usePreviewPrefill";
import useToken from "../../hooks/useToken";
import { FormsApiError, authHeader, formsFetch, isNotFound, retryServerErrors, unwrap } from "../../lib/formsApi";
import { asLocale } from "../../lib/i18n/locales";
import { withPrefill } from "../../questionnaire/answers";
import { CodeOfConductContext } from "../../questionnaire/codeOfConduct";
import type { QuestionnaireRevisionContents } from "../../questionnaire/contents";
import { FileActionsContext } from "../../questionnaire/fileActions";
import { Questionnaire } from "../../questionnaire/Questionnaire";

const PreviewPage: React.FC = () => {
    const { token } = useToken();
    const { locale } = useIntl();
    const [submitted, setSubmitted] = useState(false);
    const fileActions = usePreviewFileActions(token);
    const prefill = usePreviewPrefill(token);
    const { data, error, isPending, isPlaceholderData } = useQuery({
        queryKey: ["preview", token, locale],
        queryFn: async ({ signal }) =>
            unwrap(
                await formsFetch.GET("/api/preview", {
                    headers: authHeader(token),
                    params: { query: { lang: asLocale(locale) } },
                    signal,
                }),
                "get preview"
            ),
        placeholderData: (previous) => previous,
        retry: retryServerErrors,
        refetchOnWindowFocus: false,
    });

    const supplierName = data?.supplier.name ?? "";
    const codeOfConduct = useCodeOfConduct(token, supplierName, data?.customerName ?? "", {
        digitalSigning: true,
        signingAs: data?.receiverEmail,
        signerName: data?.receiverName,
    });

    if (error instanceof FormsApiError && error.status === 401) return <MessagePage reason="previewExpired" />;
    if (isNotFound(error)) return <MessagePage reason="404" />;
    if (error) return <MessagePage reason="other" />;

    const contents = data?.revision.contents as QuestionnaireRevisionContents | undefined;
    const customerName = data?.customerName;

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.backgroundSecondary" }}>
            <QuestionnaireTopBar
                maxWidth="md"
                translating={isPlaceholderData}
                start={
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Chip
                            size="small"
                            color="warning"
                            icon={<Eye fontSize="small" />}
                            label={<FormattedMessage defaultMessage="Preview" />}
                        />
                        <Typography variant="textSm" color="textTextSecondary" noWrap>
                            {supplierName ? (
                                <FormattedMessage
                                    defaultMessage="Answers are not saved. Shown as {supplierName}."
                                    values={{ supplierName }}
                                />
                            ) : (
                                <FormattedMessage defaultMessage="Answers are not saved." />
                            )}
                        </Typography>
                    </Stack>
                }
            />
            <Container maxWidth="md" disableGutters={false} sx={{ py: 4 }}>
                {isPending || !contents ? (
                    <Stack spacing={3}>
                        <Skeleton variant="text" width="60%" height={32} />
                        {[1, 2, 3].map((n) => (
                            <Skeleton key={n} variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
                        ))}
                    </Stack>
                ) : (
                    <FileActionsContext.Provider value={fileActions}>
                        <CodeOfConductContext.Provider value={codeOfConduct}>
                            <Questionnaire
                                contents={contents}
                                documentTypes={data?.revision.documentTypes}
                                numbers={data?.revision.numbers}
                                initialAnswers={withPrefill(contents.questions, {})}
                                onSubmit={() => setSubmitted(true)}
                                prefill={data?.prefill ? prefill : undefined}
                                intro={
                                    customerName && (
                                        <Typography variant="textSm" color="textTextSecondary">
                                            <FormattedMessage
                                                defaultMessage="{tenantName} has requested the following information from {supplierName}. Your answers help them assess and work with your company."
                                                values={{ tenantName: customerName, supplierName }}
                                            />
                                        </Typography>
                                    )
                                }
                            />
                        </CodeOfConductContext.Provider>
                    </FileActionsContext.Provider>
                )}
                <Stack sx={{ mt: 6 }}>
                    <PoweredByFooter />
                </Stack>
            </Container>
            <Snackbar
                open={submitted}
                autoHideDuration={6000}
                onClose={() => setSubmitted(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={() => setSubmitted(false)}>
                    <FormattedMessage defaultMessage="All answers are valid. In a real request, this would submit to the customer." />
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PreviewPage;
